import { Text } from "@apollosproject/canvas-ui-web";
import { ResultOf } from "@apollosproject/cluster-client";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { NavbarUserFragment, NavbarChurchFragment } from "./NavbarFragment";
import { ProfileShareMenu } from "./ProfileShareMenu";
import { UserMenu } from "./UserMenu";

const Logo = ({
  src,
  srcDark,
  alt,
}: {
  src?: string;
  srcDark?: string;
  alt?: string;
}) => (
  <div className="flex space-x-2 items-center">
    {src ? (
      <>
        <img src={src} alt={alt} className="h-6 w-auto dark:hidden" />
        <img src={srcDark} alt={alt} className="h-6 w-auto hidden dark:block" />
      </>
    ) : (
      <Text as="h3">{alt}</Text>
    )}
  </div>
);

export const Navbar = ({
  currentChurch,
  currentUser,
  currentUserCanJoinChurch = false,
}: {
  currentChurch?: ResultOf<typeof NavbarChurchFragment>;
  currentUser?: ResultOf<typeof NavbarUserFragment>;
  currentUserCanJoinChurch?: boolean;
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;
      const scrollThreshold = 10; // Minimum scroll amount before hiding

      if (Math.abs(currentScrollY - lastScrollY) > scrollThreshold) {
        setIsVisible(!scrollingDown);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const logo = currentChurch?.wordmarkLightUrl ?? undefined;
  const logoDark = currentChurch?.wordmarkDarkUrl ?? undefined;
  const name = currentChurch?.name ?? undefined;

  return (
    <>
      <nav
        className={clsx(
          "fixed w-full z-10 transition-transform duration-300 ease-in-out",
          "bg-gradient-to-b from-system-screen to-material-thin dark:from-system-screen-dark dark:to-material-thin-dark backdrop-blur-lg",
          isVisible ? "translate-y-0" : "-translate-y-full"
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-center items-center relative">
            {/* Logo centered */}
            <div className="absolute left-1/2 -translate-x-1/2">
              <Logo src={logo} srcDark={logoDark} alt={name} />
            </div>

            {/* Menu anchored to top right */}
            <div className="absolute right-0 flex gap-x-2">
              <ProfileShareMenu
                currentUser={currentUser}
                currentChurch={currentChurch}
                currentUserCanJoinChurch={currentUserCanJoinChurch}
              />

              <UserMenu currentUser={currentUser} orgName={name} />
            </div>
          </div>
        </div>
      </nav>
      <div className="h-16" /> {/* spacer for fixed navbar */}
    </>
  );
};
